import { useState, useEffect } from "react";
import { api, adminAuth, imageUrl } from "../../lib/api";
import { toast } from "sonner";
import { Search, Trash2, Download, Store, Truck, Layers, FileArchive, Repeat, CalendarRange, CheckCircle2, Circle, Mail } from "lucide-react";
import { downloadDesign } from "../../lib/designRender";
import { buildSizeRuns } from "../../lib/sizeRuns";

const SHIPPING_STATUSES = ["pending_payment", "paid", "processing", "shipped", "delivered", "cancelled"];
const PICKUP_STATUSES = ["pending_payment", "paid", "processing", "ready", "picked_up", "cancelled"];
const STATUS_LABELS = { ready: "ready for pickup", picked_up: "picked up" };
const statusLabel = (s) => STATUS_LABELS[s] || s.replace(/_/g, " ");

export default function OrdersTab({ orders, setOrders }) {
  const [orderSearch, setOrderSearch] = useState("");
  const [orderFilter, setOrderFilter] = useState("paid"); // "paid" | "awaiting" | "production" | "all"
  const [lightbox, setLightbox] = useState(null); // { images: [], index }
  const [zipping, setZipping] = useState(null); // order id currently zipping

  const downloadAllFiles = async (orderId) => {
    setZipping(orderId);
    try {
      const res = await api.get(`/admin/orders/${orderId}/download`, {
        headers: adminAuth(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `order-${orderId.slice(0, 8).toUpperCase()}-design-files.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Design files downloaded");
    } catch (e) {
      const msg = e?.response?.status === 404 ? "No design files on this order" : "Download failed";
      toast.error(msg);
    } finally {
      setZipping(null);
    }
  };

  const openImages = (item, index) => {
    const imgs = [item.customization.design_render_url, ...(item.customization.uploaded_photo_urls || [])]
      .filter(Boolean)
      .map(imageUrl);
    if (imgs.length) setLightbox({ images: imgs, index });
  };

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowRight") setLightbox((lb) => lb && { ...lb, index: (lb.index + 1) % lb.images.length });
      else if (e.key === "ArrowLeft") setLightbox((lb) => lb && { ...lb, index: (lb.index - 1 + lb.images.length) % lb.images.length });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  const updateStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}/status`, { status }, { headers: adminAuth() });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
      toast.success("Updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const updateTracking = async (orderId, tracking_number, tracking_carrier) => {
    try {
      await api.patch(`/admin/orders/${orderId}/status`, { tracking_number, tracking_carrier }, { headers: adminAuth() });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, tracking_number, tracking_carrier } : o)));
      toast.success("Tracking saved");
    } catch { toast.error("Save failed"); }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm("Delete this order permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/orders/${orderId}`, { headers: adminAuth() });
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
      toast.success("Order deleted");
    } catch { toast.error("Delete failed"); }
  };

  const [recapSending, setRecapSending] = useState(false);
  const sendRecapNow = async () => {
    setRecapSending(true);
    try {
      await api.post("/admin/send-daily-recap", {}, { headers: adminAuth() });
      toast.success("Daily recap emailed (if there were paid orders today)");
    } catch {
      toast.error("Could not send recap");
    } finally {
      setRecapSending(false);
    }
  };

  const [weeklySending, setWeeklySending] = useState(false);
  const sendWeeklyNow = async () => {
    setWeeklySending(true);
    try {
      await api.post("/admin/send-weekly-report", {}, { headers: adminAuth() });
      toast.success("Weekly report emailed (if there were paid orders last week)");
    } catch {
      toast.error("Could not send weekly report");
    } finally {
      setWeeklySending(false);
    }
  };

  const toggleMade = async (order, idx, made) => {
    setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, made_items: { ...(o.made_items || {}), [idx]: made } } : o)));
    try {
      await api.patch(`/admin/orders/${order.id}/production`, { item_index: idx, made }, { headers: adminAuth() });
    } catch {
      toast.error("Couldn't update checklist");
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, made_items: { ...(o.made_items || {}), [idx]: !made } } : o)));
    }
  };

  const madeProgress = (o) => {
    const total = (o.items || []).length;
    const done = (o.items || []).filter((_, i) => (o.made_items || {})[i]).length;
    return { done, total };
  };

  const [resending, setResending] = useState(null);
  const resendConfirmation = async (orderId) => {
    setResending(orderId);
    try {
      const res = await api.post(`/admin/orders/${orderId}/resend-confirmation`, {}, { headers: adminAuth() });
      toast.success(`Confirmation emailed to ${res.data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend confirmation");
    } finally {
      setResending(null);
    }
  };

  const isPaid = (o) => o.payment_status === "paid";
  const isAwaiting = (o) => o.payment_status !== "paid" && o.status !== "cancelled";
  const needsProduction = (o) =>
    o.payment_status === "paid" && ["paid", "processing"].includes(o.status);
  const productionCount = orders.filter(needsProduction).length;
  const paidCount = orders.filter(isPaid).length;
  const awaitingCount = orders.filter(isAwaiting).length;
  const FILTER_TITLES = { paid: "Paid Orders", awaiting: "Awaiting Payment", production: "Needs Production", all: "All Orders" };

  const filteredOrders = orders.filter((o) => {
    if (orderFilter === "paid" && !isPaid(o)) return false;
    if (orderFilter === "awaiting" && !isAwaiting(o)) return false;
    if (orderFilter === "production" && !needsProduction(o)) return false;
    if (!orderSearch.trim()) return true;
    const q = orderSearch.toLowerCase();
    return (
      o.id.toLowerCase().includes(q) ||
      o.shipping?.full_name?.toLowerCase().includes(q) ||
      o.shipping?.email?.toLowerCase().includes(q) ||
      o.shipping?.city?.toLowerCase().includes(q) ||
      o.tracking_number?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="card-rustic">
      <div className="p-5 border-b border-[rgba(27,42,65,0.1)] flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono-ui text-[10px] tracking-[0.3em] text-[var(--crimson)]">/ORDERS</div>
          <div className="font-serif-display text-2xl text-[var(--navy)] flex items-center gap-3">
            {FILTER_TITLES[orderFilter] || "All Orders"}
            {productionCount > 0 && (
              <span className="inline-flex items-center gap-1 bg-[var(--crimson)] text-white font-mono-ui text-[10px] tracking-[0.15em] uppercase px-2 py-1 rounded-full" data-testid="production-count-badge">
                {productionCount} to make
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap w-full md:w-auto">
          <button
            onClick={sendRecapNow}
            disabled={recapSending}
            data-testid="send-daily-recap-btn"
            className="inline-flex items-center gap-1.5 border border-[var(--navy)]/30 text-[var(--navy)] hover:bg-[var(--navy)] hover:text-[var(--bone)] disabled:opacity-50 px-3 py-2 rounded-sm font-mono-ui text-[10px] tracking-[0.15em] uppercase transition"
          >
            <Download className="w-3.5 h-3.5"/> {recapSending ? "Sending…" : "Send Recap Now"}
          </button>
          <button
            onClick={sendWeeklyNow}
            disabled={weeklySending}
            data-testid="send-weekly-report-btn"
            className="inline-flex items-center gap-1.5 border border-[var(--navy)]/30 text-[var(--navy)] hover:bg-[var(--navy)] hover:text-[var(--bone)] disabled:opacity-50 px-3 py-2 rounded-sm font-mono-ui text-[10px] tracking-[0.15em] uppercase transition"
          >
            <CalendarRange className="w-3.5 h-3.5"/> {weeklySending ? "Sending…" : "Weekly Report"}
          </button>
          <div className="flex items-center gap-1 bg-[var(--bone-2)] rounded-sm p-1 flex-wrap" data-testid="order-filter-toggle">
            {[
              { key: "paid", label: `Paid (${paidCount})`, active: "bg-[var(--navy)] text-[var(--bone)]" },
              { key: "awaiting", label: `Awaiting Payment (${awaitingCount})`, active: "bg-[var(--navy)] text-[var(--bone)]" },
              { key: "production", label: `Needs Production (${productionCount})`, active: "bg-[var(--crimson)] text-white" },
              { key: "all", label: `All (${orders.length})`, active: "bg-[var(--navy)] text-[var(--bone)]" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setOrderFilter(f.key)}
                data-testid={`order-filter-${f.key}`}
                className={`px-3 py-1.5 rounded-sm font-mono-ui text-[10px] tracking-[0.15em] uppercase transition ${orderFilter === f.key ? f.active : "text-[var(--navy)] hover:bg-[var(--bone)]"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 bg-[var(--bone-2)] rounded-sm px-3 py-2 flex-1 md:w-72">
            <Search className="w-3.5 h-3.5 text-[var(--ink-soft)]"/>
            <input
              type="text"
              value={orderSearch}
              onChange={(e) => setOrderSearch(e.target.value)}
              placeholder="Search by name, email, ID, tracking…"
              className="bg-transparent border-0 outline-none flex-1 text-sm"
              data-testid="admin-order-search"
            />
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--bone-2)]/50 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--ink-soft)]">
              <th className="text-left p-4">Order</th>
              <th className="text-left p-4">Customer</th>
              <th className="text-left p-4">Items</th>
              <th className="text-right p-4">Total</th>
              <th className="text-left p-4">Payment</th>
              <th className="text-left p-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-[var(--ink-soft)]">{orders.length === 0 ? "No orders yet." : "No matching orders."}</td></tr>
            )}
            {filteredOrders.map((o) => (
              <tr key={o.id} className="border-t border-[rgba(27,42,65,0.06)] hover:bg-[var(--bone-2)]/30" data-testid={`order-row-${o.id}`}>
                <td className="p-4 align-top">
                  <div className="font-mono-ui text-xs text-[var(--navy)] break-all">{o.id.slice(0, 8)}…</div>
                  <div className="font-mono-ui text-[10px] text-[var(--ink-soft)] mt-1">{new Date(o.created_at).toLocaleString()}</div>
                  <div className={`mt-1.5 inline-flex items-center gap-1 font-mono-ui text-[9px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full ${o.pickup ? "bg-[var(--gold)]/25 text-[var(--navy)]" : "bg-[var(--navy)]/10 text-[var(--navy)]"}`} data-testid={`order-fulfillment-${o.id}`}>
                    {o.pickup ? <><Store className="w-3 h-3 text-[var(--crimson)]"/> Pickup</> : <><Truck className="w-3 h-3"/> Ship</>}
                  </div>
                  {needsProduction(o) && (
                    <div className="mt-1.5 inline-flex items-center gap-1 bg-[var(--crimson)]/10 text-[var(--crimson)] font-mono-ui text-[9px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full" data-testid={`order-production-badge-${o.id}`}>
                      ● Needs production
                    </div>
                  )}
                  {o.tax_exempt && (
                    <div className="mt-1.5 inline-flex items-center gap-1 bg-[var(--navy)]/10 text-[var(--navy)] font-mono-ui text-[9px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full" data-testid={`order-tax-exempt-${o.id}`} title={o.tax_exempt_certificate ? `Cert #${o.tax_exempt_certificate}` : ""}>
                      Tax Exempt{o.tax_exempt_certificate ? ` · #${o.tax_exempt_certificate}` : ""}
                    </div>
                  )}
                </td>
                <td className="p-4 align-top">
                  <div className="font-serif-display text-base text-[var(--navy)]">{o.shipping?.full_name}</div>
                  <div className="text-xs text-[var(--ink-soft)]">{o.shipping?.email}</div>
                  <div className="text-xs text-[var(--ink-soft)]">{o.shipping?.city}, {o.shipping?.state}</div>
                  {o.repeat_customer && (
                    <div className="mt-1.5 inline-flex items-center gap-1 bg-[var(--gold)]/25 text-[var(--navy)] font-mono-ui text-[9px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full" data-testid={`order-repeat-customer-${o.id}`} title={`${o.customer_order_count} paid orders from this customer`}>
                      <Repeat className="w-3 h-3 text-[var(--crimson)]"/> Repeat · {o.customer_order_count}× 
                    </div>
                  )}
                </td>
                <td className="p-4 align-top text-xs text-[var(--ink-soft)]">
                  <button
                    type="button"
                    onClick={() => downloadAllFiles(o.id)}
                    disabled={zipping === o.id}
                    data-testid={`order-download-all-${o.id}`}
                    className="mb-3 inline-flex items-center gap-1.5 bg-[var(--navy)] text-[var(--bone)] hover:bg-[var(--crimson)] disabled:opacity-50 px-2.5 py-1.5 rounded-sm font-mono-ui text-[9px] tracking-[0.2em] uppercase transition"
                  >
                    <FileArchive className="w-3 h-3"/> {zipping === o.id ? "Zipping…" : "Download all files (.zip)"}
                  </button>
                  <button
                    type="button"
                    onClick={() => resendConfirmation(o.id)}
                    disabled={resending === o.id}
                    data-testid={`order-resend-confirmation-${o.id}`}
                    className="mb-3 ml-2 inline-flex items-center gap-1.5 border border-[var(--navy)]/30 text-[var(--navy)] hover:bg-[var(--navy)] hover:text-[var(--bone)] disabled:opacity-50 px-2.5 py-1.5 rounded-sm font-mono-ui text-[9px] tracking-[0.2em] uppercase transition"
                    title="Resend the order confirmation / invoice email to the customer"
                  >
                    <Mail className="w-3 h-3"/> {resending === o.id ? "Sending…" : "Resend invoice email"}
                  </button>
                  {(() => {
                    const { done, total } = madeProgress(o);
                    if (!total) return null;
                    const allDone = done === total;
                    return (
                      <div
                        className={`mb-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-sm font-mono-ui text-[9px] tracking-[0.15em] uppercase ${allDone ? "bg-[#3f7d3f]/15 text-[#2f6b2f]" : "bg-[var(--crimson)]/10 text-[var(--crimson)]"}`}
                        data-testid={`order-production-progress-${o.id}`}
                      >
                        {allDone ? <CheckCircle2 className="w-3 h-3"/> : <Circle className="w-3 h-3"/>}
                        {allDone ? "All made ✓" : `Production ${done}/${total} made`}
                      </div>
                    );
                  })()}
                  {buildSizeRuns(o.items || []).map((run, ri) => (
                    <div key={run.key} className="mb-2 p-2 bg-[var(--navy)]/5 rounded-sm" data-testid={`order-size-run-${o.id}-${ri}`}>
                      <div className="flex items-center gap-1 font-mono-ui text-[9px] tracking-[0.15em] uppercase text-[var(--crimson)]">
                        <Layers className="w-3 h-3"/> {run.product_name}{run.descriptor ? ` · ${run.descriptor}` : ""}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {run.sizes.map((s) => (
                          <span key={s.size} className="inline-flex items-baseline gap-0.5 bg-[var(--bone-2)] rounded-sm px-1.5 py-0.5 font-mono-ui text-[10px] text-[var(--navy)]">
                            {s.size}<span className="text-[var(--crimson)] font-bold">×{s.qty}</span>
                          </span>
                        ))}
                        <span className="font-mono-ui text-[9px] tracking-[0.15em] uppercase text-[var(--ink-soft)] self-center ml-0.5">{run.totalQty} pcs</span>
                      </div>
                    </div>
                  ))}
                  {o.items?.map((i, idx) => (
                    <div key={idx} className="mb-1">
                      <label className="inline-flex items-center gap-1.5 cursor-pointer select-none mb-0.5" data-testid={`order-item-made-label-${o.id}-${idx}`}>
                        <input
                          type="checkbox"
                          checked={!!(o.made_items || {})[idx]}
                          onChange={(e) => toggleMade(o, idx, e.target.checked)}
                          data-testid={`order-item-made-${o.id}-${idx}`}
                          className="w-3.5 h-3.5 accent-[var(--crimson)] cursor-pointer"
                        />
                        <span className={`font-mono-ui text-[9px] tracking-[0.15em] uppercase ${(o.made_items || {})[idx] ? "text-[#2f6b2f]" : "text-[var(--ink-soft)]"}`}>
                          {(o.made_items || {})[idx] ? "Made ✓" : "Mark made"}
                        </span>
                      </label>
                      <div className={(o.made_items || {})[idx] ? "opacity-50" : ""}>
                      {i.quantity}× {i.product_name} <span className="opacity-70">({i.customization?.color_name}/{i.customization?.size})</span>
                      {i.customization?.layers?.length ? (
                        <div className="mt-0.5" data-testid={`order-item-layers-${o.id}-${idx}`}>
                          <div className="text-[var(--navy)]/70 font-mono-ui text-[10px]">
                            🎨 {i.customization.layers.length} layers: {["preset","image","text"].map((t) => { const n = i.customization.layers.filter((l) => l.type === t).length; return n ? `${n} ${t}` : null; }).filter(Boolean).join(" · ")}
                          </div>
                          {(i.customization.design_render_url || (i.customization.uploaded_photo_urls || []).length > 0) && (
                            <div className="flex flex-wrap gap-2 mt-1.5" data-testid={`order-item-thumbs-${o.id}-${idx}`}>
                              {i.customization.design_render_url && (
                                <button type="button" onClick={() => openImages(i, 0)} title="Combined design — click to zoom" className="block" data-testid={`order-thumb-combined-${o.id}-${idx}`}>
                                  <img src={imageUrl(i.customization.design_render_url)} alt="Combined design" className="w-14 h-14 object-contain rounded-sm border border-[var(--crimson)]/40 bg-[repeating-conic-gradient(#e5e5e5_0%_25%,#fff_0%_50%)] bg-[length:12px_12px] hover:ring-2 hover:ring-[var(--crimson)] cursor-zoom-in"/>
                                </button>
                              )}
                              {(i.customization.uploaded_photo_urls || []).map((purl, pi) => (
                                <button key={purl} type="button" onClick={() => openImages(i, (i.customization.design_render_url ? 1 : 0) + pi)} title={`Original photo ${pi + 1} — click to zoom`} className="block" data-testid={`order-thumb-photo-${o.id}-${idx}-${pi}`}>
                                  <img src={imageUrl(purl)} alt={`Photo ${pi + 1}`} className="w-14 h-14 object-cover rounded-sm border border-[rgba(27,42,65,0.2)] hover:ring-2 hover:ring-[var(--navy)] cursor-zoom-in"/>
                                </button>
                              ))}
                            </div>
                          )}
                          {i.customization.design_render_url ? (
                            <a
                              href={imageUrl(i.customization.design_render_url)}
                              target="_blank" rel="noopener noreferrer" download
                              data-testid={`order-download-design-${o.id}-${idx}`}
                              className="mt-1 inline-flex items-center gap-1.5 text-[var(--crimson)] hover:underline font-mono-ui text-[10px] tracking-[0.15em] uppercase"
                            >
                              <Download className="w-3 h-3" /> Combined print file (PNG)
                            </a>
                          ) : (
                            <button
                              onClick={() => downloadDesign(i.customization.layers, `order-${o.id.slice(0,8)}-item${idx + 1}.png`)}
                              data-testid={`order-download-design-${o.id}-${idx}`}
                              className="mt-1 inline-flex items-center gap-1.5 text-[var(--crimson)] hover:underline font-mono-ui text-[10px] tracking-[0.15em] uppercase"
                            >
                              <Download className="w-3 h-3" /> Print file (PNG)
                            </button>
                          )}
                          {(i.customization.uploaded_photo_urls || []).length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-1" data-testid={`order-item-photos-${o.id}-${idx}`}>
                              {i.customization.uploaded_photo_urls.map((purl, pi) => (
                                <a key={purl} href={imageUrl(purl)} target="_blank" rel="noopener noreferrer" download
                                   className="inline-flex items-center gap-1 text-[var(--navy)] hover:underline font-mono-ui text-[10px] tracking-[0.1em] uppercase">
                                  <Download className="w-3 h-3" /> Photo {pi + 1} (original)
                                </a>
                              ))}
                            </div>
                          )}
                          {i.customization.layers.filter((l) => l.type === "text" && (l.text || "").trim()).map((l, ti) => (
                            <div key={ti} className="text-[var(--navy)] mt-0.5 font-mono-ui text-[10px]" data-testid={`order-item-text-${o.id}-${idx}-${ti}`}>
                              ✏️ Text: <strong>“{l.text}”</strong>{l.color ? <span className="opacity-60"> · {l.color}</span> : null}
                            </div>
                          ))}
                        </div>
                      ) : i.customization?.design_transform ? (
                        <div className="text-[var(--navy)]/70 mt-0.5 font-mono-ui text-[10px]" data-testid={`order-item-placement-${o.id}-${idx}`}>
                          🎯 Placement: {Math.round(i.customization.design_transform.x)}%, {Math.round(i.customization.design_transform.y)}% · size {Math.round(i.customization.design_transform.w)}×{Math.round(i.customization.design_transform.h)}%
                        </div>
                      ) : null}
                      {i.customization?.note && (
                        <div className="text-[var(--crimson)] mt-0.5" data-testid={`order-item-note-${o.id}-${idx}`}>📝 {i.customization.note}</div>
                      )}
                      {(i.exclude_from_discounts || i.exclude_from_free_shipping) && (
                        <div className="flex flex-wrap gap-1 mt-1" data-testid={`order-item-exclusions-${o.id}-${idx}`}>
                          {i.exclude_from_discounts && <span className="bg-[var(--crimson)]/10 text-[var(--crimson)] px-1.5 py-0.5 rounded-sm font-mono-ui text-[8px] tracking-[0.15em] uppercase">No Discount</span>}
                          {i.exclude_from_free_shipping && <span className="bg-[var(--navy)]/10 text-[var(--navy)] px-1.5 py-0.5 rounded-sm font-mono-ui text-[8px] tracking-[0.15em] uppercase">Ships Separately</span>}
                        </div>
                      )}
                      </div>
                    </div>
                  ))}
                  {o.customer_note && (
                    <div className="mt-2 p-2 bg-[var(--paper-dim,#F4F1EA)] border-l-2 border-[var(--gold,#C59B6D)] rounded text-[var(--navy)]" data-testid={`order-note-${o.id}`}>
                      <span className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-[var(--crimson)] block mb-0.5">Customer Note</span>
                      {o.customer_note}
                    </div>
                  )}
                </td>
                <td className="p-4 align-top text-right font-mono-ui">${o.total?.toFixed(2)}</td>
                <td className="p-4 align-top">
                  <span className={`stamp-badge text-[9px] ${o.payment_status === "paid" ? "border-[var(--crimson)] text-[var(--crimson)]" : "border-[var(--ink-soft)] text-[var(--ink-soft)]"}`}>
                    {o.payment_status?.toUpperCase()}
                  </span>
                </td>
                <td className="p-4 align-top">
                  <select
                    value={o.status}
                    onChange={(e) => updateStatus(o.id, e.target.value)}
                    className="bg-[var(--bone)] border border-[rgba(27,42,65,0.2)] rounded-sm px-2 py-1.5 font-mono-ui text-xs"
                    data-testid={`order-status-${o.id}`}
                  >
                    {(o.pickup ? PICKUP_STATUSES : SHIPPING_STATUSES).map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                  </select>
                  {o.pickup ? (
                    <div className="mt-2 inline-flex items-center gap-1.5 bg-[var(--gold)]/20 text-[var(--navy)] rounded-sm px-2 py-1.5 font-mono-ui text-[10px] tracking-[0.1em]" data-testid={`order-pickup-note-${o.id}`}>
                      <Store className="w-3.5 h-3.5 text-[var(--crimson)]"/> Local pickup — no shipping
                    </div>
                  ) : (
                    <div className="mt-2 flex flex-col gap-1">
                      <input
                        type="text"
                        defaultValue={o.tracking_number || ""}
                        placeholder="Tracking #"
                        className="bg-[var(--bone)] border border-[rgba(27,42,65,0.2)] rounded-sm px-2 py-1 font-mono-ui text-[10px] w-40"
                        data-testid={`order-tracking-${o.id}`}
                        onBlur={(e) => {
                          const val = e.target.value.trim();
                          if (val !== (o.tracking_number || "")) updateTracking(o.id, val, o.tracking_carrier || "");
                        }}
                      />
                      <input
                        type="text"
                        defaultValue={o.tracking_carrier || ""}
                        placeholder="Carrier (USPS, UPS…)"
                        className="bg-[var(--bone)] border border-[rgba(27,42,65,0.2)] rounded-sm px-2 py-1 font-mono-ui text-[10px] w-40"
                        data-testid={`order-carrier-${o.id}`}
                        onBlur={(e) => {
                          const val = e.target.value.trim();
                          if (val !== (o.tracking_carrier || "")) updateTracking(o.id, o.tracking_number || "", val);
                        }}
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => deleteOrder(o.id)}
                    data-testid={`order-delete-${o.id}`}
                    className="mt-2 inline-flex items-center gap-1.5 text-[var(--crimson)] hover:bg-[var(--crimson)]/10 px-2 py-1 rounded-sm font-mono-ui text-[9px] tracking-[0.2em] uppercase transition"
                  >
                    <Trash2 className="w-3 h-3"/> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="order-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            data-testid="order-lightbox-close"
            className="absolute top-5 right-6 text-white/80 hover:text-white font-mono-ui text-xs tracking-[0.25em] uppercase"
          >
            ✕ Close
          </button>

          {lightbox.images.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightbox((lb) => ({ ...lb, index: (lb.index - 1 + lb.images.length) % lb.images.length })); }}
              data-testid="order-lightbox-prev"
              className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-white/15 hover:bg-white/30 text-white text-2xl"
              aria-label="Previous"
            >
              ‹
            </button>
          )}

          <img
            src={lightbox.images[lightbox.index]}
            alt="Design preview"
            onClick={(e) => e.stopPropagation()}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-sm bg-[repeating-conic-gradient(#e5e5e5_0%_25%,#fff_0%_50%)] bg-[length:20px_20px] shadow-2xl"
          />

          {lightbox.images.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightbox((lb) => ({ ...lb, index: (lb.index + 1) % lb.images.length })); }}
              data-testid="order-lightbox-next"
              className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-white/15 hover:bg-white/30 text-white text-2xl"
              aria-label="Next"
            >
              ›
            </button>
          )}

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
            {lightbox.images.length > 1 && (
              <span className="text-white/80 font-mono-ui text-[10px] tracking-[0.25em]" data-testid="order-lightbox-counter">{lightbox.index + 1} / {lightbox.images.length}</span>
            )}
            <a
              href={lightbox.images[lightbox.index]}
              target="_blank"
              rel="noopener noreferrer"
              download
              data-testid="order-lightbox-download"
              className="inline-flex items-center gap-2 bg-white text-[var(--navy)] px-4 py-2 rounded-sm font-mono-ui text-[10px] tracking-[0.25em] uppercase hover:bg-[var(--bone)]"
            >
              <Download className="w-3.5 h-3.5" /> Download Full Size
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
